/**
 * Relatórios > Financeiro — página container com abas.
 *
 * Segue o padrão de AccountingReportsView: cada aba é protegida pela sua
 * própria permissão (reports.financial_*); o usuário só vê as abas que
 * possui e a página exige ao menos uma delas.
 */
import React, { useState } from 'react';
import { CreditCard, FileSpreadsheet, PieChart, QrCode, TrendingDown, TrendingUp, Truck, Wallet } from 'lucide-react';
import { clsx } from 'clsx';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { PermissionFallback } from '@pages/financial/financialShared';
import { RevenueReportView } from './RevenueReportView';
import { ExpensesReportView } from './ExpensesReportView';
import { DreReportView } from './DreReportView';
import { CashflowReportView } from './CashflowReportView';
import { CardReceivablesView } from './CardReceivablesView';
import { PixReceivablesView } from './PixReceivablesView';
import { SuppliersReportView } from './SuppliersReportView';
import { PaymentMethodsReportView } from './PaymentMethodsReportView';

type TabId =
  | 'revenue'
  | 'expenses'
  | 'dre'
  | 'cashflow'
  | 'suppliers'
  | 'card-receivables'
  | 'pix-receivables'
  | 'payment-methods';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  permission: string;
}

const TABS: Tab[] = [
  { id: 'revenue', label: 'Receitas', icon: <TrendingUp size={16} />, permission: 'reports.financial_revenue' },
  { id: 'expenses', label: 'Despesas', icon: <TrendingDown size={16} />, permission: 'reports.financial_expenses' },
  { id: 'dre', label: 'DRE', icon: <FileSpreadsheet size={16} />, permission: 'reports.financial_dre' },
  { id: 'cashflow', label: 'Fluxo de Caixa', icon: <Wallet size={16} />, permission: 'reports.financial_cashflow' },
  // Fornecedores: parcelas dos pedidos de compra x conta corrente, por
  // fornecedor e mês, com a previsão dos próximos meses.
  { id: 'suppliers', label: 'Fornecedores', icon: <Truck size={16} />, permission: 'reports.financial_suppliers' },
  { id: 'card-receivables', label: 'Recebíveis de Cartão', icon: <CreditCard size={16} />, permission: 'reports.financial_cashflow' },
  // Recebíveis PIX usam a permissão do extrato de Conta Corrente: é a mesma
  // tela de quem concilia o dinheiro que entra na conta.
  { id: 'pix-receivables', label: 'Recebíveis PIX', icon: <QrCode size={16} />, permission: 'financial.transactions.read' },
  // Meios de pagamento das vendas: cartão à vista, 2–6x, 7–12x e PIX mês a
  // mês, e bandeiras × parcelas.
  { id: 'payment-methods', label: 'Meios de Pagamento', icon: <PieChart size={16} />, permission: 'reports.financial_payment_methods' },
];

export const FinancialReportsView: React.FC = () => {
  const { checkPermission, isLoading } = usePermissions();
  const [activeTab, setActiveTab] = useState<TabId>('revenue');

  const availableTabs = TABS.filter(tab => checkPermission(tab.permission).hasPermission);
  const effectiveTab = availableTabs.find(t => t.id === activeTab) ? activeTab : availableTabs[0]?.id;

  const renderTab = (tab: Tab, content: React.ReactNode) => (
    <PermissionGuard
      permission={tab.permission}
      fallback={<PermissionFallback label={`o relatório de ${tab.label.toLowerCase()}`} />}
    >
      {content}
    </PermissionGuard>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Financeiro</h1>
        <p className="text-sm text-slate-500 mt-1">Relatórios financeiros gerenciais</p>
      </div>

      {/* Nenhuma permissão */}
      {!isLoading && availableTabs.length === 0 && (
        <PermissionFallback label="os relatórios financeiros" />
      )}

      {/* Tabs */}
      {availableTabs.length > 1 && (
        <div className="border-b border-slate-200">
          <nav className="flex -mb-px gap-0 overflow-x-auto" aria-label="Abas do relatório financeiro">
            {availableTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'inline-flex items-center gap-2 whitespace-nowrap py-3 px-5 text-sm font-medium border-b-2 transition-colors',
                  effectiveTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab content */}
      {effectiveTab === 'revenue' && renderTab(TABS[0], <RevenueReportView />)}
      {effectiveTab === 'expenses' && renderTab(TABS[1], <ExpensesReportView />)}
      {effectiveTab === 'dre' && renderTab(TABS[2], <DreReportView />)}
      {effectiveTab === 'cashflow' && renderTab(TABS[3], <CashflowReportView />)}
      {effectiveTab === 'suppliers' && renderTab(TABS[4], <SuppliersReportView />)}
      {effectiveTab === 'card-receivables' && renderTab(TABS[5], <CardReceivablesView />)}
      {effectiveTab === 'pix-receivables' && renderTab(TABS[6], <PixReceivablesView />)}
      {effectiveTab === 'payment-methods' && renderTab(TABS[7], <PaymentMethodsReportView />)}
    </div>
  );
};

export default FinancialReportsView;
